import { createSlice } from "@reduxjs/toolkit";

const STORAGE_KEY = "influencer_finder:unlocks:v1";

const safeLoad = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const safeSave = (byCreatorId) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(byCreatorId || {}));
  } catch {
    // ignore storage failures
  }
};

const normalizeId = (id) => {
  const s = String(id ?? "").trim();
  return s || null;
};

const initialState = {
  // { [creatorId]: { unlockedUntil: number } }
  byCreatorId: safeLoad(),
};

const unlocksSlice = createSlice({
  name: "unlocks",
  initialState,
  reducers: {
    markUnlocked(state, action) {
      const rawId = action.payload?.creatorId;
      const creatorId = normalizeId(rawId);
      if (!creatorId) return;

      const ttlMsRaw = action.payload?.ttlMs;
      const ttl = Number.isFinite(Number(ttlMsRaw))
        ? Number(ttlMsRaw)
        : 24 * 60 * 60 * 1000; // default 24h

      const unlockedUntil = Date.now() + ttl;

      state.byCreatorId[creatorId] = { unlockedUntil };
      safeSave(state.byCreatorId);
    },

    // ✅ Useful when backend returns exact expiry time
    markUnlockedUntil(state, action) {
      const rawId = action.payload?.creatorId;
      const creatorId = normalizeId(rawId);
      const unlockedUntil = Number(action.payload?.unlockedUntil);

      if (!creatorId) return;
      if (!Number.isFinite(unlockedUntil)) return;

      state.byCreatorId[creatorId] = { unlockedUntil };
      safeSave(state.byCreatorId);
    },

    purgeExpired(state) {
      const now = Date.now();
      const next = {};
      for (const [id, v] of Object.entries(state.byCreatorId || {})) {
        if (v && typeof v.unlockedUntil === "number" && v.unlockedUntil > now) {
          next[id] = v;
        }
      }
      state.byCreatorId = next;
      safeSave(next);
    },

    clearUnlocked(state, action) {
      const creatorId = normalizeId(action.payload?.creatorId);
      if (!creatorId) return;
      delete state.byCreatorId[creatorId];
      safeSave(state.byCreatorId);
    },

    clearAllUnlocks(state) {
      state.byCreatorId = {};
      safeSave({});
    },
  },
});

// ✅ returns boolean
export const selectIsUnlocked = (state, creatorId) => {
  const id = String(creatorId ?? "").trim();
  if (!id) return false;

  const until = state?.unlocks?.byCreatorId?.[id]?.unlockedUntil;
  return typeof until === "number" && until > Date.now();
};

// ✅ returns timestamp (helps UI refresh displays / countdowns)
export const selectUnlockedUntil = (state, creatorId) => {
  const id = String(creatorId ?? "").trim();
  if (!id) return null;

  const until = state?.unlocks?.byCreatorId?.[id]?.unlockedUntil;
  return typeof until === "number" ? until : null;
};

export const {
  markUnlocked,
  markUnlockedUntil,
  purgeExpired,
  clearUnlocked,
  clearAllUnlocks,
} = unlocksSlice.actions;

export default unlocksSlice.reducer;
